"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

type StudentTestimonial = {
	name: string;
	facePosition: string;
	rating: number;
	title: string;
	review: string;
};

type StudentTestimonialsCarouselProps = {
	testimonials: StudentTestimonial[];
	appBaseUrl: string;
};

const fiveStarRating = [1, 2, 3, 4, 5];

function RatingStars({
	name,
	rating,
}: {
	name: string;
	rating: number;
}) {
	return (
		<div
			aria-label={`${rating.toFixed(1)} out of 5 rating`}
			className="mt-1 flex gap-0.5"
		>
			{fiveStarRating.map((star) => {
				const fillPercent = Math.max(
					0,
					Math.min(100, (rating - star + 1) * 100),
				);

				return (
					<span
						key={`${name}-${star}`}
						className="relative inline-grid size-5 text-stone-300"
					>
						<Star className="size-5 fill-current stroke-current" />
						<span
							className="absolute inset-0 overflow-hidden text-amber-400"
							style={{ width: `${fillPercent}%` }}
						>
							<Star className="size-5 fill-current stroke-current" />
						</span>
					</span>
				);
			})}
		</div>
	);
}

export default function StudentTestimonialsCarousel({
	testimonials,
	appBaseUrl,
}: StudentTestimonialsCarouselProps) {
	const viewportRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [visibleCount, setVisibleCount] = useState(1);
	const maxIndex = Math.max(0, testimonials.length - visibleCount);
	const canNavigate = maxIndex > 0;

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;

		const updateVisibleCount = () => {
			const width = viewport.offsetWidth;
			const isMobile = window.matchMedia("(max-width: 767px)").matches;
			const nextVisibleCount = Math.max(
				1,
				isMobile
					? 1
					: Math.min(
							testimonials.length,
							Math.floor((width + 24) / 300),
						),
			);

			setVisibleCount(nextVisibleCount);
			setActiveIndex((currentIndex) =>
				Math.min(currentIndex, testimonials.length - nextVisibleCount),
			);
		};

		updateVisibleCount();

		const resizeObserver = new ResizeObserver(updateVisibleCount);
		resizeObserver.observe(viewport);

		return () => resizeObserver.disconnect();
	}, [testimonials.length]);

	useEffect(() => {
		if (maxIndex === 0) return;

		const intervalId = window.setInterval(() => {
			setActiveIndex((currentIndex) =>
				currentIndex >= maxIndex ? 0 : currentIndex + 1,
			);
		}, 3000);

		return () => window.clearInterval(intervalId);
	}, [maxIndex]);

	const showPreviousTestimonial = () => {
		setActiveIndex((currentIndex) =>
			currentIndex <= 0 ? maxIndex : currentIndex - 1,
		);
	};

	const showNextTestimonial = () => {
		setActiveIndex((currentIndex) =>
			currentIndex >= maxIndex ? 0 : currentIndex + 1,
		);
	};

	return (
		<div className="relative mt-12">
			<div ref={viewportRef} className="overflow-hidden">
				<div
					className="flex transition-transform duration-700 ease-out"
					style={{
						width: `${(testimonials.length / visibleCount) * 100}%`,
						transform: `translateX(-${(activeIndex * 100) / testimonials.length}%)`,
					}}
				>
					{testimonials.map((testimonial) => (
						<div
							key={testimonial.name}
							className="min-w-0 px-2.5 xl:px-3"
							style={{ width: `${100 / testimonials.length}%` }}
						>
							<article className="flex min-h-[312px] flex-col rounded-lg border border-stone-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.10)]">
								<div className="flex items-center gap-4">
									<div
										role="img"
										aria-label={`${testimonial.name} profile photo`}
										className="size-14 shrink-0 rounded-full border border-stone-200 bg-white bg-[url('/assets/home-page-10/indian-students-professionals.webp')] shadow-[0_8px_18px_rgba(15,23,42,0.16)]"
										style={{
											backgroundPosition:
												testimonial.facePosition,
											backgroundSize: "520% auto",
										}}
									/>
									<div>
										<h3 className="text-[1rem] font-semibold text-stone-900">
											{testimonial.name}
										</h3>
										<RatingStars
											name={testimonial.name}
											rating={testimonial.rating}
										/>
									</div>
								</div>

								<div className="mt-10 flex flex-1 flex-col">
									<h3 className="text-[1.08rem] font-bold leading-7 text-stone-950">
										{testimonial.title}
									</h3>
									<p className="mt-3 line-clamp-4 text-[0.98rem] leading-7 text-stone-700">
										{testimonial.review}
									</p>
									<a
										href={appBaseUrl}
										className="mt-auto self-end pt-5 text-[0.95rem] font-semibold text-blue-600 hover:text-blue-700"
									>
										More
									</a>
								</div>
							</article>
						</div>
					))}
				</div>
			</div>

			{canNavigate ? (
				<>
					<button
						type="button"
						aria-label="Show previous testimonial"
						onClick={showPreviousTestimonial}
						className="absolute left-2 top-1/2 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-stone-200 bg-white/95 text-stone-800 shadow-[0_10px_24px_rgba(15,23,42,0.18)] transition hover:border-blue-200 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:-left-2"
					>
						<ChevronLeft className="size-5" />
					</button>

					<button
						type="button"
						aria-label="Show next testimonial"
						onClick={showNextTestimonial}
						className="absolute right-2 top-1/2 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-stone-200 bg-white/95 text-stone-800 shadow-[0_10px_24px_rgba(15,23,42,0.18)] transition hover:border-blue-200 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:-right-2"
					>
						<ChevronRight className="size-5" />
					</button>
				</>
			) : null}
		</div>
	);
}
